import assert from 'assert'
import { dateFns, dateTimeFns, Iso, monthDayFns, timeFns } from 'iso-fns2'
import _ from 'lodash'
import { isoZ, z } from 'my-zod'
import { DayOfWeek, polarisRentalProductPricing, PriceUnit } from '../polaris-rental-product/polaris-rental-product-pricing'
import { PricingPeriod } from '../polaris-rental-product/polaris-rental-product-model'
import { getPercentageAndRemainder } from '../../finance/transfers'
import { AddOnProductPriceUnitType } from '../add-on-product'
import { match } from 'ts-pattern'
import { ReservationSchemas } from '../reservation'
import { centsToDollars } from '../../internal'

export interface PriceableReservation {
  id: string
  beginDate: Iso.Date
  endDate: Iso.Date
  ridingStartTime: Iso.Time
  ridingEndTime: Iso.Time
  assetFamilyId: string
  quotedDateLocal: Iso.Date
  quotedTimeLocal: Iso.Time
  numberOfUnitsAvailable: number
}

export const priceableReservationSchema = z.object({
  id: z.string().min(1),
  beginDate: isoZ.date(),
  endDate: isoZ.date(),
  ridingStartTime: isoZ.time(),
  ridingEndTime: isoZ.time(),
  assetFamilyId: z.string(),
  quotedDateLocal: isoZ.date(),
  quotedTimeLocal: isoZ.time(),
  numberOfUnitsAvailable: z.number().int()
})

export function parseReservationAsPriceable(reservation: {
  id: string | null | undefined
  beginDate: Iso.Date | null | undefined
  endDate: Iso.Date | null | undefined
  ridingStartTime: Iso.Time | null | undefined
  ridingEndTime: Iso.Time | null | undefined
  assetFamilyId: string | null | undefined
  quotedDateLocal: Iso.Date | null | undefined
  quotedTimeLocal: Iso.Time | null | undefined
  numberOfUnitsAvailable: number | null | undefined
}): PriceableReservation {
  return priceableReservationSchema.parse(reservation)
}

export function isReservationPriceable(reservation: {
  id: string | null | undefined
  beginDate: Iso.Date | null | undefined
  endDate: Iso.Date | null | undefined
  ridingStartTime: Iso.Time | null | undefined
  ridingEndTime: Iso.Time | null | undefined
  assetFamilyId: string | null | undefined
  quotedDateLocal: Iso.Date | null | undefined
  quotedTimeLocal: Iso.Time | null | undefined
  numberOfUnitsAvailable: number | null | undefined
}): reservation is PriceableReservation {
  return priceableReservationSchema.safeParse(reservation).success
}

export function getReservationPrice({
  reservation,
  product: { pricing, pricingPeriod }
}: {
  reservation: PriceableReservation
  product: {
    pricing: polarisRentalProductPricing[]
    pricingPeriod: PricingPeriod
  }
}): number {
  assert.ok(pricing.length, 'No Prices Supplied')
  pricing = _.sortBy(pricing, (p) => p.priority)
  const start = reservation.ridingStartTime
    ? dateFns.toDateTime(reservation.beginDate, reservation.ridingStartTime)
    : dateFns.toDateTime(reservation.beginDate)
  const end = reservation.ridingEndTime
    ? dateFns.toDateTime(reservation.endDate, reservation.ridingEndTime)
    : dateFns.toDateTime(reservation.endDate)
  const assetFamilyId = reservation.assetFamilyId
  const quotedHoursBeforeReservationStart = getQuotedHoursBeforeReservationStart(reservation)
  const numberOfUnitsAvailable = reservation.numberOfUnitsAvailable

  function GetPriceForDate(pricing: polarisRentalProductPricing[], date: Iso.Date, duration: number) {
    const matches = pricing.filter(
      (p) =>
        DoesDateMatch(p, date) &&
        DoesDayOfWeekMatch(p, date) &&
        DoesAssetFamilyMatch(p, assetFamilyId) &&
        DoesDurationMatch(p, duration) &&
        DoesQuotedHoursBeforeReservationStartMatch(p, quotedHoursBeforeReservationStart) &&
        DoesMaxUnitsAvailableMatch(p, numberOfUnitsAvailable)
    )
    const match = _.maxBy(matches, 'priority')
    assert.ok(match, 'No price found!')

    if (match.unit === 'fixed') {
      return match.amount
    } else {
      const basePrice = GetPriceForDate(
        pricing.filter((p) => p.priority < match.priority),
        date,
        duration
      )
      return handleRelativePriceChange({ basePrice, priceConfiguration: match })
    }
  }
  function GetPriceForTime(pricing: polarisRentalProductPricing[], date: Iso.Date, time: Iso.DateTime, duration: number) {
    const matches = pricing.filter(
      (p) =>
        DoesDateMatch(p, date) &&
        DoesDayOfWeekMatch(p, date) &&
        DoesAssetFamilyMatch(p, reservation.assetFamilyId) &&
        DoesTimeMatch(p, time) &&
        DoesDurationMatch(p, duration) &&
        DoesQuotedHoursBeforeReservationStartMatch(p, quotedHoursBeforeReservationStart) &&
        DoesMaxUnitsAvailableMatch(p, numberOfUnitsAvailable)
    )
    const match = _.maxBy(matches, 'priority')
    assert.ok(match, 'No price found!')
    if (match.unit === 'fixed') {
      return match.amount
    } else {
      const basePrice = GetPriceForTime(
        pricing.filter((p) => p.priority < match.priority),
        date,
        time,
        duration
      )
      return handleRelativePriceChange({ basePrice, priceConfiguration: match })
    }
  }

  if (pricingPeriod === 'Daily') {
    const startDate = dateTimeFns.toDate(start)

    const hours = dateTimeFns
      .chain(start)
      .until(end, { largestUnit: 'hour', smallestUnit: 'hour', roundingMode: 'floor' })
      .getHours()
      .value()
    const duration = Math.max(Math.ceil(hours / 24), 1)

    const allDates: Iso.Date[] = []
    for (let i = 0; i < duration; i++) {
      allDates.push(dateFns.add(startDate, { days: i }))
    }
    const pricesForDates = allDates.map((d) => GetPriceForDate(pricing, d, duration))
    const totalPrice: number = _.sum(pricesForDates)
    return totalPrice
  } else if (pricingPeriod === 'Hourly') {
    const startTime = dateTimeFns.chain(start).round({ roundingMode: 'floor', smallestUnit: 'hour' }).value()
    const endTime = dateTimeFns.chain(end).round({ roundingMode: 'floor', smallestUnit: 'hour' }).value()
    const duration = dateTimeFns
      .chain(startTime)
      .until(endTime, { smallestUnit: 'hour', largestUnit: 'hour' })
      .getHours()
      .value()
    const allTimes: Iso.DateTime[] = []
    let cur = startTime
    while (dateTimeFns.isBefore(cur, endTime)) {
      allTimes.push(cur)
      cur = dateTimeFns.add(cur, { hours: 1 })
    }

    const totalPrice = _.sum(allTimes.map((d) => GetPriceForTime(pricing, dateTimeFns.toDate(d), d, duration)))
    return totalPrice
  } else if (pricingPeriod === 'Total') {
    const duration = dateTimeFns
      .chain(start)
      .until(end, { roundingMode: 'floor', smallestUnit: 'hour', largestUnit: 'hour' })
      .getHours()
      .value()
    return getReservationPriceForParameters(pricing, {
      duration,
      start,
      quotedHoursBeforeReservationStart,
      assetFamilyId,
      numberOfUnitsAvailable
    })
  } else {
    throw new Error(`Invalid Pricing Period: ${pricingPeriod}`)
  }
}

export function getAddOnComputedProductPrice({
  reservation,
  addOnProduct,
  reservationOrderLines
}: {
  reservation: {
    beginDate: Iso.Date | null
    endDate: Iso.Date | null
    vehicles: Pick<ReservationSchemas.reservationModel['vehicles'][number], 'id'>[]
  } | null
  addOnProduct: {
    priceUnit: AddOnProductPriceUnitType
    price: number
  }
  reservationOrderLines: { id: string; amount: number }[] | null
}): number {
  if (addOnProduct.priceUnit === 'fixed') {
    return addOnProduct.price
  }

  assert.ok(reservation, 'No reservation details provided')

  return match(addOnProduct.priceUnit)
    .with('fixedPerVehicle', () =>
      reservation.vehicles?.length ? addOnProduct.price * reservation.vehicles?.length : addOnProduct.price
    )
    .with('fixedPerVehiclePerDay', () => {
      const days =
        (reservation.beginDate && reservation.endDate
          ? dateFns
              .chain(reservation.beginDate)
              .until(reservation.endDate, { largestUnit: 'day', smallestUnit: 'day' })
              .getDays()
              .value()
          : 0) + 1
      return addOnProduct.price * reservation.vehicles?.length * days
    })
    .with('percentageOfReservationSubtotal', () => {
      if (!reservationOrderLines?.length) {
        // If no reservation order lines are provided, return the price as 0
        return 0
      }

      const reservationSubtotal = _.sumBy(reservationOrderLines, (l) => l.amount)

      const percentage = addOnProduct.price // in this case, the price is a percentage

      const amountInCents = Math.round((percentage / 100) * reservationSubtotal)

      return centsToDollars(amountInCents)
    })
    .otherwise(() => addOnProduct.price)
}

function DoesDateMatch(price: { startDate: Iso.MonthDay | null; endDate: Iso.MonthDay | null }, date: Iso.Date) {
  const year = dateFns.getYear(date)
  if (price.startDate && price.endDate) {
    if (monthDayFns.chain(price.startDate).toDate(year).isEqualOrBefore(monthDayFns.toDate(price.endDate, year))) {
      const startDate = monthDayFns.toDate(price.startDate, year)
      const endDate = monthDayFns.toDate(price.endDate, year)
      return dateFns.isEqualOrBefore(startDate, date) && dateFns.isEqualOrAfter(endDate, date)
    } else {
      const startDate = monthDayFns.toDate(price.startDate, year)
      const endDate = monthDayFns.toDate(price.endDate, year + 1)
      return dateFns.isEqualOrBefore(startDate, date) && dateFns.isEqualOrAfter(endDate, date)
    }
  } else if (price.startDate) {
    const startDate = monthDayFns.toDate(price.startDate, year)
    return dateFns.isEqualOrBefore(startDate, date)
  } else if (price.endDate) {
    const endDate = monthDayFns.toDate(price.endDate, year)
    return dateFns.isEqualOrAfter(endDate, date)
  } else {
    return true
  }
}

function DoesDayOfWeekMatch(price: { daysOfWeek: DayOfWeek[] | null }, date: Iso.Date) {
  if (price.daysOfWeek?.length) {
    const dayOfWeekMap: { [key in DayOfWeek]: number } = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 7
    }

    const dayOfWeek = dateFns.getDayOfWeek(date)
    return price.daysOfWeek.some((d) => dayOfWeekMap[d] === dayOfWeek)
  } else {
    return true
  }
}
function DoesAssetFamilyMatch(price: { assetFamilyIds: string[] | null }, assetFamilyId: string) {
  if (price.assetFamilyIds?.length) {
    return price.assetFamilyIds.includes(assetFamilyId)
  } else {
    return true
  }
}
function DoesTimeMatch(price: { startTime: Iso.Time | null; endTime: Iso.Time | null }, dateTime: Iso.DateTime) {
  const date = dateTimeFns.toDate(dateTime)
  const tomorrow = dateTimeFns.chain(dateTime).toDate().add({ days: 1 }).value()

  if (price.startTime && price.endTime) {
    if (timeFns.chain(price.startTime).toDateTime(date).isEqualOrBefore(timeFns.toDateTime(price.endTime, date))) {
      const startTime = timeFns.toDateTime(price.startTime, date)
      const endTime = timeFns.toDateTime(price.endTime, date)
      return dateTimeFns.isEqualOrBefore(startTime, dateTime) && dateTimeFns.isAfter(endTime, dateTime)
    } else {
      const startTime = timeFns.toDateTime(price.startTime, date)
      const endTime = timeFns.toDateTime(price.endTime, tomorrow)
      return dateTimeFns.isEqualOrBefore(startTime, dateTime) && dateTimeFns.isAfter(endTime, dateTime)
    }
  } else if (price.startTime) {
    const startTime = timeFns.toDateTime(price.startTime, date)
    return dateTimeFns.isEqualOrBefore(startTime, dateTime)
  } else if (price.endTime) {
    const endTime = timeFns.toDateTime(price.endTime, date)
    return dateTimeFns.isAfter(endTime, dateTime)
  } else {
    return true
  }
}
function DoesDurationMatch(price: { minDuration: number | null }, duration: number) {
  if (price.minDuration) {
    return duration >= price.minDuration
  } else {
    return true
  }
}

function getQuotedHoursBeforeReservationStart(reservation: {
  quotedDateLocal: Iso.Date
  quotedTimeLocal: Iso.Time
  beginDate: Iso.Date
  ridingStartTime: Iso.Time
}) {
  const quoted = dateFns.toDateTime(reservation.quotedDateLocal, reservation.quotedTimeLocal)
  const reservationStart = dateFns.toDateTime(reservation.beginDate, reservation.ridingStartTime)

  const hoursBefore = dateTimeFns
    .chain(quoted)
    .until(reservationStart, { largestUnit: 'hour', smallestUnit: 'hour', roundingMode: 'floor' })
    .getHours()
    .value()
  return hoursBefore
}

function DoesQuotedHoursBeforeReservationStartMatch(
  price: { maxHoursBeforeReservation: number | null },
  quotedHoursBeforeReservationStart: number
) {
  if (price.maxHoursBeforeReservation) {
    return quotedHoursBeforeReservationStart < price.maxHoursBeforeReservation
  } else {
    return true
  }
}

function DoesMaxUnitsAvailableMatch(price: { maxAvailableUnits: number | null }, numberOfUnitsAvailable: number) {
  if (price.maxAvailableUnits) {
    return numberOfUnitsAvailable < price.maxAvailableUnits
  } else {
    return true
  }
}

function handleRelativePriceChange({
  priceConfiguration,
  basePrice
}: {
  basePrice: number
  priceConfiguration: { amount: number; unit: PriceUnit }
}) {
  if (priceConfiguration.unit === 'fixed') {
    throw new Error('Fixed unit is not a relative change')
  } else if (priceConfiguration.unit === 'percentIncrease') {
    return getPercentageAndRemainder({
      total: basePrice,
      percentage: 1 + priceConfiguration.amount / 100
    }).amount
  } else if (priceConfiguration.unit === 'percentDecrease') {
    return getPercentageAndRemainder({
      total: basePrice,
      percentage: 1 - priceConfiguration.amount / 100
    }).amount
  } else if (priceConfiguration.unit === 'fixedIncrease') {
    return basePrice + priceConfiguration.amount
  } else if (priceConfiguration.unit === 'fixedDecrease') {
    return Math.max(basePrice - priceConfiguration.amount, 0)
  } else {
    // type test
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const typeTest: never = priceConfiguration.unit
  }
}
export function getReservationPriceForParameters(
  pricing: polarisRentalProductPricing[],
  params: {
    start: Iso.DateTime
    assetFamilyId: string
    duration: number
    quotedHoursBeforeReservationStart: number
    numberOfUnitsAvailable: number
  }
) {
  const matches = pricing.filter(
    (p) =>
      DoesDateMatch(p, dateTimeFns.toDate(params.start)) &&
      DoesDayOfWeekMatch(p, dateTimeFns.toDate(params.start)) &&
      DoesAssetFamilyMatch(p, params.assetFamilyId) &&
      DoesTimeMatch(p, params.start) &&
      DoesDurationMatch(p, params.duration) &&
      DoesQuotedHoursBeforeReservationStartMatch(p, params.quotedHoursBeforeReservationStart) &&
      DoesMaxUnitsAvailableMatch(p, params.numberOfUnitsAvailable)
  )
  const basePrice = _.maxBy(
    matches.filter((m) => m.unit === 'fixed'),
    'priority'
  )
  assert.ok(basePrice, 'No base price found!')
  const relativeChanges = _.sortBy(
    matches.filter((m) => m.unit !== 'fixed' && m.priority > basePrice.priority),
    'priority'
  )
  const withRelativeChangesApplied = relativeChanges.reduce(
    (acc, cur) => handleRelativePriceChange({ basePrice: acc, priceConfiguration: cur })!,
    basePrice.amount
  )

  return withRelativeChangesApplied
}

export const productPricingFunctionMatchers = {
  DoesDateMatch,
  DoesDayOfWeekMatch,
  DoesAssetFamilyMatch,
  DoesTimeMatch,
  DoesDurationMatch,
  DoesQuotedHoursBeforeReservationStartMatch,
  DoesMaxUnitsAvailableMatch
}
